import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { isNumber } from 'lodash';
import { EMPTY, Observable, Subject, catchError, combineLatest, firstValueFrom, mergeMap, of } from 'rxjs';
import { concatMap, map, switchMap, take, tap } from 'rxjs/operators';

import { FileUploadsService } from '@app/services/file-uploads.service';
import { RequiredRatioType, RestapiService } from '@app/services/restapi.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { selectActiveUsers } from '@main-application/administration/store/selectors/administration.selectors';
import { QueryType } from '@main-application/boards/services/turn-step-schedule.service';
import { InspectionsType } from '@main-application/inspections/components/inspections-list/inspections-list.component';
import {
  InspectionBackUrl,
  InspectionBaseUrlParamsKeys,
  InspectionModel,
} from '@main-application/inspections/models/inspection.model';
import { InspectionsHeaderViewEnum } from '@main-application/inspections/models/inspections-header.model';
import { InspectionViewEnum } from '@main-application/inspections/models/inspections-views';
import {
  InspectionSummaryDownloadReport,
  InspectionSummaryFilters,
  InspectionSummaryStatsResponse,
} from '@main-application/inspections/models/legacy-inspection-summary.model';
import { RestTimezone } from '@main-application/management/pages/system-configuration/components/date-time-configuration/model/timezone';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { TimezoneEntityHelper } from '@main-application/management/pages/system-configuration/components/timezone-entity.helper';
import {
  InspectionSummaryReportQueryParams,
  SummaryReportOffline,
} from '@main-application/shared/services/summary-report-settings.service';
import { selectUserData, selectUserPermissionLevel } from '@main-application/store/selectors/user.selectors';
import { RegeneratedInspections } from '@main-application/turnovers/interfaces/inspection.interface';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { RoutePath } from '@shared/enums/route-path.enum';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { currentLabelMe, getSortedUserItems } from '@shared/functions/get-user-list.function';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestAddInspectionTicket, RestUpdateInspectionTicket } from '@shared/interfaces/ticket.interface';
import { RestTicketModel } from '@shared/interfaces/turnover.interface';
import { Upload } from '@shared/interfaces/upload';
import { setStorageItem } from '@shared/utils/extensions';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import {
  InspectionsSummaryStatsDetails,
  SummaryFilterItem,
  SummaryFilters,
  SummaryStatsGroup,
} from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

import { InspectionSummaryNotifyAgainPayload } from '../components/inspections-list/components/legacy-inspections-summary/inspections-summary.model';
import {
  InspectionHasIssues,
  InspectionQueryType,
  InspectionsListConstants,
} from '../components/inspections-list/constants';
import { SendInspectionComponent } from '../components/send-inspection/send-inspection.component';
import {
  BulkInspectionsRequestParams,
  InspectionAppointment,
  InspectionAttachmentPayload,
  InspectionAttachmentType,
  InspectionByStatusFilter,
  InspectionCheckForDuplicateReqModel,
  InspectionDelegateReqI,
  InspectionStatus,
  InspectionType,
  RestInspectionAttachmentResponse,
  RestInspectionContents,
  RestInspectionCreateModel,
  RestInspectionUpdateModel,
  RestInspectionsModel,
  RestTypedInspectionAttachment,
  SendInspectionReportRequest,
} from '../models/rest-inspections-model.interface';
import { compareValues } from '../utils/inspection-mappers';

interface SignaturePayload {
  residentSigned?: boolean;
  residentSignName?: string;
}

export enum InspectionSummaryDisplayType {
  NA,
  Type,
  Assignee,
}

export enum InspectionsSummaryGroupType {
  None = -1,
  Template = 0,
  Assignee = 1,
  Property = 2,
  PropertyGroup = 3,
  Category = 4,
  UnitType = 5,
  Inspection = 6,
  HasIssues = 7,
  Status = 8,
  ParentInspectionId = 9,
  DynamicInspectionId = 10,
  PortfolioId = 11,
  IsArchived = 12,
}

export enum IsArchivedFilter {
  No = 0,
  Yes = 1,
}

export enum CustomFieldValueModifier {
  Raw = 0,
  DropDownGroupValue = 1,
  DropDownItemValue = 2,
  DateTimeQuarter = 3,
  DateTimeMonthYear = 4,
  DateTimeYear = 5,
}

export type SummaryStatsV2Response = SummaryStatsGroup[];

export interface InspectionSearchFilter {
  doNotReturnContent?: boolean;
  startDate?: string;
  endDate?: string;
  queryType?: QueryType;
  portfolioId?: number;
  propertyIds?: string;
  pageSize?: number;
  offset?: number;
  periodInDays?: number;
  inspectionTemplateId?: number;
  inspectionTemplateCategoryId?: number;
  unitType?: string;
  excludeCompletedAndCancelled?: boolean;
  isResident?: boolean;
  includeArchived?: boolean;
  returnStandardAndResidentBoth?: boolean;
  inReviewOnly?: boolean;
  scheduleAppointmentIsRequired?: boolean;
  modifiedSince?: string;
  parentInspectionId?: number;
  inspectionByStatusFilter?: InspectionByStatusFilter;
  periodDateSearchFields?: PeriodDateSearchFields;
  filters?: SummaryFilterItem<InspectionsSummaryGroupType>[];
  diligenceId?: number;
  dynamicInspectionId?: number;
  hasNonDefaultOrNAAnswersLeft?: InspectionHasIssues;
}

export enum PeriodDateSearchFields {
  AnyFields,
  DueDateOnly,
}

export interface InspectionFilter {
  doNotReturnContent?: boolean;
  startDate?: string;
  endDate?: string;
  queryType?: QueryType;
  portfolioId?: number;
  propertyIds?: string;
  pageSize?: number;
  offset?: number;
  periodInDays?: number;
  inspectionTemplateId?: number;
  inspectionTemplateCategoryId?: number;
  unitType?: string;
  excludeCompletedAndCancelled?: boolean;
  isResident?: boolean;
  includeArchived?: boolean;
  returnStandardAndResidentBoth?: boolean;
  inReviewOnly?: boolean;
  scheduleAppointmentIsRequired?: boolean;
  modifiedSince?: string;
  parentInspectionId?: number;
  inspectionByStatusFilter?: InspectionByStatusFilter;
  periodDateSearchFields?: number;
  filters?: SummaryFilterItem[];
  diligenceId?: number;
  dynamicInspectionId?: number;
}

export interface SummaryStatsV2FilterOption {
  id: string;
  label: string;
}

const DO_CHECK_INTERVAL = 3000;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class InspectionService extends PuSubscribable {
  private _currentSurvey: RestInspectionContents | undefined;
  downloadOptions$ = new Subject<RegeneratedInspections>();
  fetchOneTimeInspectionList$ = new Subject<void>();
  private submissionQueue$ = new Subject<{ payload: RestInspectionContents; ack: Subject<void> }>();
  private queueInitialized = false;

  get survey() {
    return this._currentSurvey;
  }

  constructor(
    private readonly snackbarService: SnackbarService,
    private restApiService: RestapiService,
    private readonly matDialog: MatDialog,
    private modalService: ModalsService,
    private router: Router,
    private store: Store,
    private fileUploadService: FileUploadsService,
    private timezoneService: TimezoneService
  ) {
    super();
  }

  private static fixTimezoneForRestInspectionsModelList(data: RestInspectionsModel[], timezone: RestTimezone) {
    data.forEach(e => TimezoneEntityHelper.fixTimezoneForInspectionDataModelToClient(e, timezone));
    return data;
  }

  list(
    portfolioId?: number,
    startDate?: string,
    endDate?: string,
    inspectionType?: InspectionsType,
    returnStandardAndResidentBoth?: boolean,
    onlyArchived?: boolean,
    periodDateSearchFields?: InspectionsListConstants.TimeWindow,
    inspectionByStatusFilter?: number,
    dynamicInspectionId?: number,
    categoryIds?: number[],
    diligenceId?: number,
    hasNonDefaultOrNAAnswersLeft?: InspectionHasIssues
  ): Observable<RestInspectionsModel[]> {
    if (!startDate && (inspectionType === InspectionsType.User || inspectionType === InspectionsType.Resident)) {
      startDate = '2022-01-01';
    }

    let url = `inspections/list?portfolioId=${portfolioId}&doNotReturnContent=true`;

    if (diligenceId) {
      url += `&diligenceId=${diligenceId}&startDate=${startDate}`;
    } else {
      if (inspectionType === InspectionsType.User || inspectionType === InspectionsType.Resident) {
        url += `&startDate=${startDate}&endDate=${endDate || ''}`;
      }

      if (inspectionType === InspectionsType.Resident) {
        url += `&isResident=${true}`;
      }

      if (returnStandardAndResidentBoth) {
        url += `&returnStandardAndResidentBoth=${returnStandardAndResidentBoth}`;
      }

      if (onlyArchived) {
        url += `&queryType=${InspectionQueryType.Archived}&includeArchived=true`;
      } else {
        url += `&queryType=${InspectionQueryType.Active}&includeArchived=false`;
      }

      if (periodDateSearchFields === InspectionsListConstants.TimeWindow.DueDateOnly) {
        url += `&periodDateSearchFields=${InspectionsListConstants.TimeWindow.DueDateOnly}`;
      }

      if (inspectionByStatusFilter) {
        url += `&inspectionByStatusFilter=${inspectionByStatusFilter}`;
      }

      if (dynamicInspectionId) {
        url += `&dynamicInspectionId=${dynamicInspectionId}`;
      }

      if (categoryIds != null) {
        if (Array.isArray(categoryIds)) {
          url += `&inspectionTemplateCategoryIds=${categoryIds.map(encodeURIComponent).join(',')}`;
        } else {
          url += `&inspectionTemplateCategoryIds=${encodeURIComponent(categoryIds)}`;
        }
      }

      if (isNumber(hasNonDefaultOrNAAnswersLeft) && hasNonDefaultOrNAAnswersLeft !== InspectionHasIssues.Any) {
        url += `&hasNonDefaultOrNAAnswersLeft=${hasNonDefaultOrNAAnswersLeft}`;
      }
    }

    return this.restApiService.getData<RestInspectionsModel[]>(
      url,
      InspectionService.fixTimezoneForRestInspectionsModelList
    );
  }

  inReviewList(
    portfolioId?: number,
    isResident?: boolean,
    returnStandardAndResidentBoth?: boolean,
    onlyArchived?: boolean
  ): Observable<RestInspectionsModel[]> {
    let url = `inspections/list?portfolioId=${portfolioId}&doNotReturnContent=true&inReviewOnly=true`;

    if (typeof isResident !== 'undefined') {
      url += `&isResident=${isResident}`;
    }

    if (returnStandardAndResidentBoth) {
      url += `&returnStandardAndResidentBoth=${returnStandardAndResidentBoth}`;
    }

    if (onlyArchived) {
      url += `&queryType=${InspectionQueryType.Archived}&includeArchived=true`;
    } else {
      url += `&queryType=${InspectionQueryType.Active}&includeArchived=false`;
    }

    return this.restApiService.getData<RestInspectionsModel[]>(
      url,
      InspectionService.fixTimezoneForRestInspectionsModelList
    );
  }

  repeating(onlyArchived = false): Observable<RestInspectionsModel[]> {
    return this.restApiService.getData<RestInspectionsModel[]>(
      `inspections/recurring?doNotReturnContent=true${onlyArchived ? '&queryType=2' : '&queryType=1'}`,
      InspectionService.fixTimezoneForRestInspectionsModelList
    );
  }

  getInspectionsByParent(parentId: number, startDate?: Date): Observable<RestInspectionsModel[]> {
    const startDateParam = startDate instanceof Date ? `&startDate=${startDate.toISOString().slice(0, 10)}` : '';
    return this.restApiService.getData<RestInspectionsModel[]>(
      `inspections/list?parentInspectionId=${parentId}&doNotReturnContent=true${startDateParam}`,
      InspectionService.fixTimezoneForRestInspectionsModelList
    );
  }

  getBulkInspections(params: BulkInspectionsRequestParams): Observable<RestInspectionsModel[]> {
    const start = params.startDate ? `&startDate=${params.startDate.toISOString().slice(0, 10)}` : '';
    const end = params.endDate ? `&endDate=${params.endDate.toISOString().slice(0, 10)}` : '';
    return this.restApiService.getData<RestInspectionsModel[]>(
      `inspections/bulk?includeRecurringBulk=false&doNotReturnContent=true&queryType=${
        params.queryType || 0
      }${start}${end}`,
      InspectionService.fixTimezoneForRestInspectionsModelList
    );
  }

  updateCalendarView(inspectionModel: RestInspectionsModel) {
    return this.restApiService.post<void>(
      `inspections/updateCalendarView/${inspectionModel.id}`,
      {
        calendarPosition: inspectionModel.calendarPosition,
        dueDate: inspectionModel.dueDate,
      },
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToServer
    );
  }

  updateInspectionComment(inspectionId: number, contentId: number, pageIndex: number, newComment: string) {
    return this.restApiService.post<void>(`inspections/${inspectionId}/${contentId}/updateComment`, {
      pageIndex,
      newComment,
    });
  }

  attachInspectionFiles(
    inspectionId: number,
    attachments: InspectionAttachmentPayload[]
  ): Observable<RestInspectionAttachmentResponse[]> {
    return this.restApiService.post<RestInspectionAttachmentResponse[]>(
      `inspections/${inspectionId}/attach`,
      attachments
    );
  }

  attachInspectionContentFiles(
    inspectionId: number,
    contentId: number,
    attachments: InspectionAttachmentPayload[]
  ): Observable<RestInspectionAttachmentResponse[]> {
    return this.restApiService.post<RestInspectionAttachmentResponse[]>(
      `inspections/${inspectionId}/${contentId}/attach`,
      attachments
    );
  }

  removeInspectionAttachments(
    inspectionId: number,
    contentId: number,
    pageIndex: number,
    fileUploadIds: number[]
  ): Observable<void> {
    const payload: number[] = fileUploadIds;
    return this.restApiService.delete<void>(`inspections/${inspectionId}/${contentId}/${pageIndex}/attach`, payload);
  }

  uploadAndAttachFileToInspection(
    file: File,
    inspectionId: number,
    contentId: number,
    pageIndex: number,
    requiredRatio?: RequiredRatioType,
    requiredRationMin?: RequiredRatioType
  ): Observable<Upload> {
    const route = `fileUploads/attachToInspection?InspectionId=${inspectionId}&ContentId=${contentId}&PageIndex=${pageIndex}`;
    return this.restApiService.upload<Upload>(route, file, undefined, requiredRatio, requiredRationMin);
  }

  completeFileUploadAndAttachToInspection(
    blobUrl: string,
    inspectionId: number,
    contentId: number,
    pageIndex: number,
    options: { fileName?: string; thumbnailId?: number; videoDuration?: number } = {}
  ): Observable<any> {
    const query = new URLSearchParams({
      InspectionId: String(inspectionId),
      ContentId: String(contentId),
      PageIndex: String(pageIndex),
    });
    if (options.fileName) query.append('fileName', options.fileName);
    if (options.thumbnailId) query.append('thumbnailFileUploadId', String(options.thumbnailId));
    if (options.videoDuration) query.append('videoDurationInSeconds', String(options.videoDuration));

    return this.restApiService.post<any>(`fileUploads/completeFileUploadAndAttachToInspection?${query.toString()}`, {
      blobUrl,
    });
  }

  create(inspectionModel: RestInspectionCreateModel): Observable<RestInspectionsModel> {
    return this.restApiService.create<RestInspectionsModel>(
      `inspections/add`,
      inspectionModel,
      null,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  update(inspectionModel: RestInspectionUpdateModel): Observable<RestInspectionsModel> {
    return this.restApiService.create<RestInspectionsModel>(
      `inspections/${inspectionModel.id}`,
      inspectionModel,
      null,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  extendExpiration(reqData: { newDate: Date }, id: number): Observable<RestInspectionsModel> {
    return this.restApiService.create<RestInspectionsModel>(
      `inspections/extendExpireDate/${id}`,
      reqData,
      TimezoneEntityHelper.fixTimezoneForExtendExpirationModelToServer,
      TimezoneEntityHelper.fixTimezoneForExtendExpirationModelToClient
    );
  }

  delete(inspectionId: number): Observable<any> {
    return this.restApiService.delete<void>(`inspections/${inspectionId}`);
  }

  regenerate(inspectionId: number): Observable<void> {
    return this.restApiService.create<void>(`inspections/${inspectionId}/regeneratePdf`, {});
  }

  generateEmptyForm(inspectionId: number): Observable<void> {
    return this.restApiService.getData<void>(`inspections/${inspectionId}/generateEmptyForm`);
  }

  getById(inspectionId: number) {
    return this.restApiService.getData<RestInspectionsModel>(
      `inspections/${inspectionId}`,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  getBeUnitId(unitId: number) {
    return this.restApiService.getData<RestInspectionsModel[]>(`inspections/ByUnit/${unitId}`);
  }

  async openSendDialog(id: number, inspectionTitle: string) {
    const config: MatDialogConfig = {
      minWidth: '500px',
      panelClass: 'send-inspection-report-modal-container',
      data: inspectionTitle,
    };

    const sendRequest = await firstValueFrom(
      this.matDialog
        .open<SendInspectionComponent, typeof config, SendInspectionReportRequest>(SendInspectionComponent, config)
        .afterClosed()
    );

    if (sendRequest) {
      this.sendReport(id, sendRequest)
        .subscribe({
          next: () => this.snackbarService.success(SnackbarErrorMessage.InspectionReportSentSuccessfully),
          error: error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorSendingInspectionReport);
            console.error(error);
          },
        })
        .untilDestroyed(this);
    }
  }

  sendReport(inspectionId: number, request: SendInspectionReportRequest) {
    return this.restApiService.post<RestInspectionsModel>(`inspections/${inspectionId}/sendReport`, request);
  }

  onRegenerate(inspection: InspectionModel, regenerateAction: string, generateEmpty = false): void {
    const { attachments } = inspection.entityDetails;

    const hasEmptyForm =
      attachments?.none?.some(a => a.inspectionAttachmentType === InspectionAttachmentType.EmptyForm) ||
      attachments?.attachment?.inspectionAttachmentType === InspectionAttachmentType.EmptyForm;

    const hasStandardReport = !!attachments?.attachment?.url;
    const hasAutoScope = !!attachments?.autoScopeReport?.url;
    const hasNspire = !!attachments?.nSpireReport?.url;

    if (generateEmpty && hasEmptyForm) {
      this.downloadOptions$.next({
        inspectionAttachmentType: InspectionAttachmentType.EmptyForm,
        autoScopeInspectionAttachmentType: null,
        nSpireInspectionAttachmentType: null,
        inspectionId: inspection.id,
      });
      this.snackbarService.success(
        `Form ready! <a class="cursor-pointer underline text-color white">
        <span data-toast-action="${regenerateAction}" 
              data-toast-action-param="${inspection.id}" 
              data-toast-second-param="${InspectionAttachmentType.EmptyForm}">
          Download
        </span>
      </a>`
      );
      return;
    }

    const generate$ = generateEmpty ? this.generateEmptyForm(inspection.id) : this.regenerate(inspection.id);

    generate$
      .pipe(
        catchError(err => {
          this.snackbarService.error(generateEmpty ? 'Error generating PDF form' : 'Error regenerating PDF report');
          throw err;
        }),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.snackbarService.dismiss();
        setTimeout(() => this.snackbarService.snackbarLoadingSpinner$.next(true), 200);
        this.snackbarService.snackbarLoadingSpinner$.next(true);
        this.snackbarService.info(
          generateEmpty ? 'Your form is generating…' : 'Your report is generating…',
          null,
          false
        );

        this.doCheckReportIsReady(inspection.id, attachments?.attachment, regenerateAction, generateEmpty);
      })
      .untilDestroyed(this);
  }

  private doCheckReportIsReady(
    inspectionId: number,
    attachment: RestTypedInspectionAttachment,
    regenerateAction: string,
    generateEmpty = false
  ) {
    this.getById(inspectionId)
      .pipe(untilDestroyed(this))
      .subscribe(inspection => {
        const att = inspection?.attachments;
        const hasEmptyForm =
          att?.attachment?.inspectionAttachmentType === InspectionAttachmentType.EmptyForm ||
          att?.none?.some(a => a.inspectionAttachmentType === InspectionAttachmentType.EmptyForm);

        const hasReport = !!att?.attachment?.url || !!att?.autoScopeReport?.url || !!att?.nSpireReport?.url;

        if (generateEmpty) {
          if (hasEmptyForm) {
            const foundAttachment =
              att?.attachment?.inspectionAttachmentType === InspectionAttachmentType.EmptyForm
                ? att.attachment
                : att.none?.find(a => a.inspectionAttachmentType === InspectionAttachmentType.EmptyForm);

            this.downloadOptions$.next({
              inspectionAttachmentType: InspectionAttachmentType.EmptyForm,
              autoScopeInspectionAttachmentType: null,
              nSpireInspectionAttachmentType: null,
              inspectionId,
            });

            this.snackbarService.snackbarLoadingSpinner$.next(false);
            this.snackbarService.dismiss();

            this.snackbarService.success(
              `Form ready! <a class="cursor-pointer underline text-color white">
              <span data-toast-action="${regenerateAction}"
                    data-toast-action-param="${inspectionId}"
                    data-toast-second-param="${InspectionAttachmentType.EmptyForm}">
                Download
              </span>
            </a>`
            );
            return;
          }

          setTimeout(() => {
            this.doCheckReportIsReady(inspectionId, attachment, regenerateAction, true);
          }, DO_CHECK_INTERVAL);
          return;
        }

        if (hasReport) {
          const inspectionAttachmentType = att?.attachment?.inspectionAttachmentType || null;
          const autoScopeInspectionAttachmentType = att?.autoScopeReport?.inspectionAttachmentType || null;
          const nSpireInspectionAttachmentType = att?.nSpireReport?.inspectionAttachmentType || null;
          const nFullSpireInspectionAttachmentType = att?.fullNSpireReport?.inspectionAttachmentType || null;

          this.downloadOptions$.next({
            inspectionAttachmentType,
            autoScopeInspectionAttachmentType,
            nSpireInspectionAttachmentType,
            inspectionId,
          });
          this.snackbarService.snackbarLoadingSpinner$.next(false);
          this.snackbarService.dismiss();

          const downloadLinks: string[] = [];
          if (inspectionAttachmentType)
            downloadLinks.push(`<a class="cursor-pointer underline text-color white">
            <span data-toast-action="${regenerateAction}" data-toast-action-param="${inspectionId}"
              data-toast-second-param="${InspectionAttachmentType.Attachment}">
              Download standard report
            </span></a>`);
          if (autoScopeInspectionAttachmentType)
            downloadLinks.push(`<a class="cursor-pointer underline text-color white">
            <span data-toast-action="${regenerateAction}" data-toast-action-param="${inspectionId}"
              data-toast-second-param="${InspectionAttachmentType.AutoScopeReport}">
              Download auto scope report
            </span></a>`);
          if (nSpireInspectionAttachmentType)
            downloadLinks.push(`<a class="cursor-pointer underline text-color white">
            <span data-toast-action="${regenerateAction}" data-toast-action-param="${inspectionId}"
              data-toast-second-param="${InspectionAttachmentType.NSpireReport}">
              Download NSPIRE Report (issues only)
            </span></a>`);
          if (nFullSpireInspectionAttachmentType)
            downloadLinks.push(`<a class="cursor-pointer underline text-color white">
            <span data-toast-action="${regenerateAction}" data-toast-action-param="${inspectionId}"
              data-toast-second-param="${InspectionAttachmentType.FullNspireReport}">
              Download NSPIRE Report (full)
            </span></a>`);

          this.snackbarService.success(`Report ready! ${downloadLinks.join('&nbsp;&nbsp;')}`);
          return;
        }

        setTimeout(() => {
          this.doCheckReportIsReady(inspectionId, attachment, regenerateAction);
        }, DO_CHECK_INTERVAL);
      })
      .untilDestroyed(this);
  }

  downloadInspectionSummaryReport(settings: InspectionSummaryReportQueryParams): Observable<SummaryReportOffline> {
    return this.restApiService.post<SummaryReportOffline>('inspections/exportSummary/offline', settings);
  }

  public notifyAgainWithParams(payload: InspectionSummaryNotifyAgainPayload): Observable<void> {
    return this.restApiService.post<void>(`inspections/notifyAgain`, payload);
  }

  public onDownloadInspection(inspectionId: number, inspectionType: InspectionAttachmentType): void {
    const download = (inspection: InspectionModel) => {
      if (
        inspectionType !== InspectionAttachmentType.EmptyForm &&
        inspection.entityDetails.status !== InspectionStatus.Completed &&
        inspection.entityDetails.status !== InspectionStatus.Expired
      ) {
        this.snackbarService.error(SnackbarErrorMessage.InspectionMustBeCompleted);
        return;
      }

      if (
        inspectionType === InspectionAttachmentType.EmptyForm &&
        inspection.entityDetails.attachments?.none?.some(
          a => a.inspectionAttachmentType === InspectionAttachmentType.EmptyForm
        )
      ) {
        const fileUploadId = inspection.entityDetails.attachments?.none?.find(
          a => a.inspectionAttachmentType === InspectionAttachmentType.EmptyForm
        )?.fileUploadId;
        this.fileUploadService.downloadFile(fileUploadId);
      } else if (
        inspectionType === InspectionAttachmentType.NSpireReport &&
        inspection.entityDetails.attachments?.nSpireReport
      ) {
        const { fileUploadId } = inspection.entityDetails.attachments?.nSpireReport;
        this.fileUploadService.downloadFile(fileUploadId);
      } else if (
        inspectionType === InspectionAttachmentType.FullNspireReport &&
        inspection.entityDetails.attachments?.fullNSpireReport
      ) {
        const { fileUploadId } = inspection.entityDetails.attachments?.fullNSpireReport;
        this.fileUploadService.downloadFile(fileUploadId);
      } else if (
        inspectionType === InspectionAttachmentType.AutoScopeReport &&
        inspection.entityDetails.attachments?.autoScopeReport
      ) {
        const { fileUploadId } = inspection.entityDetails.attachments?.autoScopeReport;
        this.fileUploadService.downloadFile(fileUploadId);
      } else {
        if (inspection.entityDetails.attachments?.attachment) {
          const { fileUploadId } = inspection.entityDetails.attachments?.attachment;
          this.fileUploadService.downloadFile(fileUploadId);
        }
      }

      if (
        inspectionType === InspectionAttachmentType.EmptyForm &&
        inspection.entityDetails.attachments?.none?.some(
          a => a.inspectionAttachmentType === InspectionAttachmentType.EmptyForm
        )
      ) {
        return;
      }

      if (
        !inspection.entityDetails.attachments?.attachment?.url &&
        !inspection.entityDetails.attachments?.autoScopeReport?.url &&
        !inspection.entityDetails.attachments?.nSpireReport?.url &&
        !inspection.entityDetails.attachments?.fullNSpireReport?.url
      ) {
        this.snackbarService.error(SnackbarErrorMessage.InspectionPdfNotFound);
      }
    };

    this.getById(inspectionId)
      .pipe(untilDestroyed(this))
      .subscribe(inspection => {
        this.snackbarService.resetSnackbarActions();
        download(new InspectionModel(inspection, null));
      })
      .untilDestroyed(this);
  }

  deleteInspection(inspection: InspectionModel, noConfirm = false) {
    const deleteFn = this.delete(inspection.id).pipe(
      catchError(err => {
        this.snackbarService.error('Error deleting inspection');
        throw err;
      })
    );

    if (noConfirm) {
      return deleteFn;
    }

    return this.modalService
      .openConfirmationModal(
        {
          title: `Delete inspection?`,
          content: `Delete ${inspection.inspectionTemplate?.name || 'inspection'} for ${inspection.propertyName} - ${
            inspection.unitName
          }?`,
          confirmColor: 'warn',
          confirmLabel: 'Delete',
        },
        'sm'
      )
      .afterClosed()
      .pipe(mergeMap(result => (result === DialogResult.Success ? deleteFn : of(true))));
  }

  tableSortPredicate(inspection: InspectionModel, sortHeaderId: string) {
    switch (sortHeaderId) {
      case 'unit.name':
        return inspection.entityDetails.unit ? inspection.entityDetails.unit.name?.toLocaleLowerCase() : 'all units';
      case 'turboMode':
        return inspection.entityDetails.isTurboMode;
      case 'unit.propertyName':
        return inspection.propertyName?.toLocaleLowerCase();
      case 'inspectionType':
        return (inspection.inspectionTemplate?.name ?? inspection.inspectionType)?.toLocaleLowerCase();
      case 'rooms':
        return inspection.completedOrSkippedAreasCount;
      case 'areas':
        return inspection.areasCount;
      case 'assignee':
        return inspection.entityDetails.assigneeUser?.displayName?.toLocaleLowerCase();
      case 'createdAt':
        return inspection.entityDetails.createdAt;
      case 'completedAt':
        return inspection.entityDetails.completedAt;
      case 'dueDate':
        return inspection.entityDetails.dueDate;
      case 'expired':
        return inspection.entityDetails.autoExpiredOn;
      case 'until':
        return inspection.entityDetails.endDate;
      case 'repeats':
        return inspection?.parentInspectionId !== null && inspection?.parentInspectionId > 0 ? 'Yes' : 'No';
      case 'reviewer':
        return inspection.reviewer?.displayName?.toLocaleLowerCase();
      case 'reviewCompletedByUser':
        return inspection.reviewer?.displayName?.toLocaleLowerCase();
      case 'nextDue':
        return inspection.dontShowDue ? '-' : inspection.entityDetails.dueDate?.toISOString();
      default:
        return inspection[sortHeaderId];
    }
  }

  sortByPrimaryKeyAndUnit(inspections: InspectionModel[], sort: MatSort): InspectionModel[] {
    const data = inspections.slice();
    if (!sort.active || sort.direction === '') {
      return data;
    }
    return data.sort((a, b) => {
      const primaryA = this.tableSortPredicate(a, sort.active);
      const primaryB = this.tableSortPredicate(b, sort.active);
      const primaryCompare = compareValues(primaryA, primaryB, sort.direction === 'asc');
      if (primaryCompare !== 0 || sort.active === 'unit.name') {
        return primaryCompare;
      }
      return compareValues(this.tableSortPredicate(a, 'unit.name'), this.tableSortPredicate(b, 'unit.name'), true);
    });
  }

  sortByPrimaryKey(inspections: InspectionModel[], sort: MatSort): InspectionModel[] {
    const data = inspections.slice();
    if (!sort.active || sort.direction === '') {
      return data;
    }
    return data.sort((a, b) => {
      const primaryA = this.tableSortPredicate(a, sort.active);
      const primaryB = this.tableSortPredicate(b, sort.active);
      return compareValues(primaryA, primaryB, sort.direction === 'asc');
    });
  }

  open(
    inspection: InspectionModel,
    backInspectionUrl?: InspectionBackUrl,
    inspectionByStatusFilter?: InspectionByStatusFilter,
    reportHash?: string
  ) {
    let url: string;
    url = backInspectionUrl != null ? backInspectionUrl : null;

    if (
      inspection?.entityDetails?.diligenceId &&
      inspection?.status !== 'Completed' &&
      inspection?.status !== 'Canceled' &&
      inspection?.status !== 'Expired' &&
      inspection?.status !== 'Review'
    ) {
      this.router.navigate([RoutePath.DILIGENCE, inspection.entityDetails.diligenceId, inspection.id], {
        queryParams: {
          [InspectionBaseUrlParamsKeys.backInspectionUrl]: url,
        },
        fragment: reportHash?.replace(/^#/, ''),
      });
      return;
    } else {
      if (url && reportHash && inspectionByStatusFilter) {
        url += `/report/${inspectionByStatusFilter}`;
      } else if (url && inspectionByStatusFilter) {
        url += `/${inspectionByStatusFilter}`;
      }
    }

    inspection.entityDetails.inspectionType === InspectionType.CustomInspection
      ? this.router.navigate([RoutePath.CUSTOM_INSPECTIONS, inspection.entityDetails.id], {
          queryParamsHandling: 'merge',
          queryParams: {
            [InspectionBaseUrlParamsKeys.backInspectionUrl]: url,
          },
          fragment: reportHash?.replace(/^#/, ''),
        })
      : this.router.navigate([RoutePath.INSPECTIONS, inspection.entityDetails.id], {
          queryParamsHandling: 'merge',
        });
  }

  share(inspection: InspectionModel, evt?: Event) {
    if (evt) {
      evt.preventDefault();
    }

    if (inspection.entityDetails.status === InspectionStatus.Completed) {
      this.openSendDialog(
        inspection.entityDetails.id,
        `${inspection.inspectionType} inspection: ${inspection.entityDetails.unit.propertyName} - ${inspection.entityDetails.unit.name}`
      );
    } else {
      this.snackbarService.error(`You can only share a completed inspection`);
    }
  }

  download(inspectionId: number, inspectionType?: InspectionAttachmentType): void {
    this.onDownloadInspection(inspectionId, inspectionType);
  }

  createTicket(ticket: RestAddInspectionTicket) {
    return this.restApiService.create<RestTicketModel>('tickets/v2/add', ticket);
  }

  updateTicket(ticket: RestUpdateInspectionTicket) {
    return this.restApiService.create<void>('tickets/v2/' + ticket.id, ticket);
  }

  getTicketsForInspection(inspectionId: number): Observable<RestTicketModel[]> {
    return this.restApiService.getData<RestTicketModel[]>('tickets/ByInspectionId/' + inspectionId);
  }

  getResidentInspections(): Observable<RestInspectionsModel[]> {
    return this.restApiService.getData<RestInspectionsModel[]>('inspections/residentInspections');
  }

  getSummaryStats(payload: InspectionSummaryFilters): Observable<InspectionSummaryStatsResponse> {
    return this.restApiService.post<InspectionSummaryStatsResponse>('inspections/summaryStats', payload);
  }

  getInspectionsSummaryStats(filter: SummaryFilters): Observable<SummaryStatsGroup[]> {
    let url = `inspections/summaryStats/${filter.groupBy ?? 0}/${filter.subGroupBy ?? 0}?`;

    if (filter.isResident) url += `&isResident=${filter.isResident}`;
    if (filter.portfolioId !== null && filter.portfolioId !== undefined) url += `&portfolioId=${filter.portfolioId}`;
    if (filter.collectionIds) url += `&collectionIds=${filter.collectionIds}`;
    if (filter.propertyIds) url += `&propertyIds=${filter.propertyIds}`;
    if (filter.startDate) url += `&startDate=${filter.startDate}`;
    if (filter.endDate) url += `&endDate=${filter.endDate}`;

    return this.restApiService.getData<SummaryStatsGroup[]>(url);
  }

  getInspectionsSummaryDetails(filter: SummaryFilters): Observable<InspectionsSummaryStatsDetails> {
    let url = `inspections/summaryStatsDetails?`;

    if (filter.isResident) url += `&isResident=${filter.isResident}`;
    if (filter.portfolioId !== null && filter.portfolioId !== undefined) url += `&portfolioId=${filter.portfolioId}`;
    if (filter.collectionIds) url += `&collectionIds=${filter.collectionIds}`;
    if (filter.propertyIds) url += `&propertyIds=${filter.propertyIds}`;
    if (filter.startDate) url += `&startDate=${filter.startDate}`;
    if (filter.endDate) url += `&endDate=${filter.endDate}`;

    return this.restApiService.getData<InspectionsSummaryStatsDetails>(url).pipe(
      map(data => {
        data.byType = data.byType ?? [];
        data.byAssignee = data.byAssignee?.map(el => ({ ...el, isAssignee: true })) ?? [];
        return data;
      })
    );
  }

  markAsCompleted(
    inspectionId: number,
    residentSigned?: boolean,
    residentSignName?: string
  ): Observable<RestInspectionsModel> {
    return this.store.select(selectUserPermissionLevel).pipe(
      take(1),
      map(permissionLevel =>
        checkPermissionLevel(permissionLevel, PermissionLevelType.InspectionsList_MarkAsCompleted)
      ),
      switchMap(permissionLevel => {
        if (!permissionLevel) {
          this.snackbarService.error(
            SnackbarErrorMessage.PermissionRequired.replace(
              '{PERMISSION}',
              PermissionLevelType.InspectionsList_MarkAsCompleted
            ).replace('{ACTION}', 'complete inspection')
          );
          return of(null);
        }
        const payload: SignaturePayload = {};

        if (residentSigned && residentSignName) {
          payload.residentSigned = residentSigned;
          payload.residentSignName = residentSignName;
        }
        return this.completeInspection(inspectionId, payload);
      }),
      catchError(err => {
        const error = err.error?.errorCode ?? err.error?.message;
        this.snackbarService.error(error || SnackbarErrorMessage.SomethingWentWrong);
        return of(null);
      })
    );
  }

  completeInspection(inspectionId: number, payload: SignaturePayload = {}): Observable<RestInspectionsModel> {
    return this.restApiService.post(
      `inspections/${inspectionId}/complete`,
      payload,
      null,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  archiveInspection(inspectionId: number): Observable<RestInspectionsModel> {
    return this.restApiService.post(
      `inspections/${inspectionId}/archive`,
      null,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToServer,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  unArchiveInspection(inspectionId: number): Observable<RestInspectionsModel> {
    return this.restApiService.post(
      `inspections/${inspectionId}/unarchive`,
      null,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToServer,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  inspectionDelegate(id: number, model: InspectionDelegateReqI): Observable<RestInspectionsModel> {
    return this.restApiService.create<RestInspectionsModel>(
      `inspections/${id}/delegate`,
      model,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToServer,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  finishReview(id: number): Observable<RestInspectionsModel> {
    return this.restApiService.create<RestInspectionsModel>(
      `inspections/${id}/finishReview`,
      null,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToServer,
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient
    );
  }

  queueSubmitAnswers(payload: RestInspectionContents): Observable<void> {
    if (!this.queueInitialized) {
      this.initializeSubmissionQueue();
    }
    const ack = new Subject<void>();
    this.submissionQueue$.next({ payload, ack });
    return ack.asObservable();
  }

  private initializeSubmissionQueue(): void {
    if (this.queueInitialized) return;
    this.queueInitialized = true;

    this.submissionQueue$
      .pipe(
        concatMap(({ payload, ack }) =>
          this.submitAnswers(payload).pipe(
            tap(() => {
              ack.next();
              ack.complete();
            }),
            catchError(err => {
              ack.error(err);
              return of(null);
            })
          )
        )
      )
      .subscribe()
      .untilDestroyed(this);
  }

  submitAnswers(payload: RestInspectionContents): Observable<any> {
    return this.restApiService.post<RestInspectionContents>('inspections/submitAnswers', payload);
  }

  notifyAgain(inspectionId: number): Observable<void> {
    return this.restApiService.post<void>(`inspections/${inspectionId}/notifyAgain`);
  }

  onDownloadInspectionSummary(payload: InspectionSummaryDownloadReport): Observable<any> {
    return this.restApiService.downloadWithPost<any>('inspections/summaryreport', payload);
  }

  updateAppointment(inspectionId: number, payload: InspectionAppointment): Observable<void> {
    return this.restApiService.post<void>(`inspections/${inspectionId}/appointment`, payload);
  }

  getAppointmentHours(date?: Date): { value: Date; label: string }[] {
    const today = date || this.timezoneService.getCurrentDateOnly();
    const appointments: { value: Date; label: string }[] = [];

    appointments.push({
      value: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0),
      label: '12:00 AM',
    });

    for (let hour = 1; hour <= 11; hour++) {
      const label = `${hour}:00 AM`;
      const appointmentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, 0, 0);
      appointments.push({ value: appointmentDate, label });
    }

    appointments.push({
      value: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0),
      label: '12:00 PM',
    });

    for (let hour = 1; hour <= 11; hour++) {
      const label = `${hour}:00 PM`;
      const appointmentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour + 12, 0, 0);
      appointments.push({ value: appointmentDate, label });
    }

    return appointments;
  }

  getSortedUsers(): Observable<IRadioButtonOption<number>[]> {
    return combineLatest([this.store.select(selectUserData), this.store.select(selectActiveUsers)]).pipe(
      map(([userData, activeUsers]) => {
        const sortedList = [{ value: -1, label: 'Unassigned' }].concat(getSortedUserItems(activeUsers));
        const currentUserIndex = sortedList.findIndex(user => user.value === userData.id);
        if (currentUserIndex > 1) {
          const [currentUser] = sortedList.splice(currentUserIndex, 1);
          sortedList.splice(1, 0, { ...currentUser, label: currentLabelMe(currentUser.label) });
        }

        return sortedList;
      })
    );
  }

  public updateInspectionsViewModeInStorage(type: InspectionsHeaderViewEnum): void {
    setStorageItem(LocalStorageDataEnum.INSPECTION_VIEW_MODE, {
      [InspectionViewEnum.INSPECTION_VIEW]: type,
    });
  }

  getInspectionsList(filter: InspectionSearchFilter): Observable<RestInspectionsModel[]> {
    return this.restApiService.post<RestInspectionsModel[]>('inspections/list', filter).pipe(
      catchError(err => {
        const error = err?.errorCode ?? SnackbarErrorMessage.RetrievingTicketList;
        this.snackbarService.error(error);
        return of([]);
      })
    );
  }
  checkForDuplicates(payload: InspectionCheckForDuplicateReqModel): Observable<RestInspectionsModel[]> {
    return this.restApiService.post<RestInspectionsModel[]>(`inspections/duplicateCheck`, payload);
  }

  inspectionsBulkArchive(ids: number[]) {
    return this.restApiService.post<RestInspectionsModel>(`inspections/bulkArchive`, { ids }).pipe(
      catchError(err => {
        const error = err.error?.errorCode ?? err.error?.message;
        this.snackbarService.error(error || SnackbarErrorMessage.SomethingWentWrong);
        return EMPTY;
      })
    );
  }

  getTurnoverInspections(id: number): Observable<RestInspectionsModel[]> {
    return this.restApiService.getData<RestInspectionsModel[]>('inspections/turnover/' + id);
  }
}
